import { useAuth0 } from '@auth0/auth0-react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import { ProtectedRoute, RoleBasedRoute, SideMenu } from '../components';

import {
  AccessDenied,
  Activation,
  AllFinancials,
  AutomotiveDivision,
  AutomotiveDivisionLocations,
  BCPLanding,
  BCPProcessAreas,
  BCPSelectionPage,
  BusinessFunctions,
  BusinessFunctionsApplicationDescription,
  BusinessFunctionsTagging,
  BusinessFunctionsTaggingBulk,
  BusinessFunctionsTaggingITSelection,
  BusinessFunctionsTaggingSelection,
  BusinessFunctionWorkarounds,
  BusinessFunctionWorkaroundsItApplications,
  CorporateView,
  CrisisCommunication,
  CyberHubPage,
  CyberHubItApplications,
  CyberHubBusinessFunctionsItApplications,
  CyberHubQualitativeFactorsItApplications,
  CyberHubProcessAreasItApplications,
  CyberMenu,
  DataInputs,
  DependenciesTree,
  DependencyMapping,
  DisasterRecoveryPlan,
  DivisionDetail,
  DivisionFinancials,
  DivisionSelection,
  DivisionView,
  EmergencyResponsePlan,
  EnterpriseApplications,
  EnterpriseApplicationsLanding,
  EnterpriseApplicationsSummary,
  ExposureQuantification,
  ExposureSummary,
  ExternalMitigationTable,
  FinancialsCorporate,
  FinishedGoodsInventory,
  FinishedGoodsLocations,
  FinishedGoodsMapping,
  FinishedGoodsSummary,
  HostingLocations,
  InternalExternalMitigation,
  InternalMitigationTable,
  ITApplications,
  ItResources,
  LevelLocationMapping,
  LevelLocations,
  LevelSummary,
  LocationApplicationMapping,
  LocationApplicationSelection,
  LocationApplicationSummary,
  LocationBusinessFunctions,
  LocationBusinessFunctionsSelection,
  LocationBusinessFunctionsITSelection,
  LocationBusinessFunctionsApplicationDescription,
  LocationBusinessFunctionsBulk,
  LocationLevelLanding,
  LocationLevelList,
  LocationLevelOptions,
  LocationLevelSelection,
  LocationProcessAreasSummary,
  LocationProcessAreasTagging,
  Locations,
  LocationSelection,
  LoginPage,
  Management,
  Mission,
  MitigatingLocations,
  MitigationLocationSelection,
  NotFound,
  OtherConsiderations,
  PALocation,
  PALocationSelection,
  PAMitigation,
  PeoplePA,
  ProcessAreas,
  ProcessAreasDependencies,
  ProcessAreasSummary,
  Processes,
  ProcessesAndEquipment,
  ProductFinancials,
  ProductGroupFinancials,
  ProductGroupView,
  Production,
  ProductView,
  QualitativeFactors,
  QualitativeFactorsBulk,
  QualitativeFactorsDescription,
  QualitativeFactorsOverview,
  QualitativeFactorsSelection,
  RiskHub,
  RolesDetail,
  Strategies,
  StrategyFacility,
  StrategyReview,
  StrategySummary,
  SummaryProcessArea,
  SummaryProcesses,
  SummarySuppliers,
  SupplierDependencies,
  SupplierDependenciesSummary,
  SupplierMapping,
  Suppliers,
  SuppliersMappingPercentages,
  SupplyChainPA,
  UserDetail,
  UsersManagement,
  ApplicationWorkaroundsLanding,
  WorkaroundsBusinessFunctionsList,
  WorkaroundsBusinessFunctionsSummary,
  LocationLevelApplicationWorkarounds,
  CyberHubBusinessFunctionsReport,
  CyberHubQualitativeFactorsReport,
  CyberHubProcessAreasReport,
  CyberHubItApplicationReport,
} from '../pages';
import { useUserContext } from '../context/PermissionsContext';
import {
  EnterpriseApplicationsList,
  LocationProcessAreas,
  LocationProcessAreasSelection,
  LocationQualitativeFactors,
  LocationQualitativeFactorsDescription,
  LocationQualitativeFactorsSelection,
  QualitativeFactorWorkarounds,
  QualitativeFactorWorkaroundStats,
  ProcessAreaAndProcessesWorkarounds,
  ProcessAreaOrProcessWorkarounds,
  LocationQualitativeFactorsBulk,
} from '../pages/cyber';
import { TiersDashboard } from '../pages/dataInputs/tiers';
import ApplicationWorkaround from '../pages/cyber/pages/workarounds/ApplicationWorkaround';

const AppRoutes = () => {
  const { isAuthenticated } = useAuth0();
  const { isActive, isAdmin } = useUserContext();

  const routes = createBrowserRouter([
    {
      path: '/',
      element: isAuthenticated ? (
        <Navigate to="/risk-hub" />
      ) : (
        <Navigate to="/login" />
      ),
    },
    {
      path: '/login',
      element: isAuthenticated ? <Navigate to="/risk-hub" /> : <LoginPage />,
    },
    {
      element: (
        <ProtectedRoute>
          <SideMenu />
        </ProtectedRoute>
      ),
      children: [
        {
          path: '/users-management',
          element: (
            <RoleBasedRoute permissions={[isActive, isAdmin]}>
              <UsersManagement />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/users-management/:userId',
          element: (
            <RoleBasedRoute permissions={[isActive, isAdmin]}>
              <UserDetail />
            </RoleBasedRoute>
          ),
        },
        // {
        //   path: '/roles-management',
        //   element: (
        //     <RoleBasedRoute permissions={[isActive, isAdmin]}>
        //       <RolesManagement />
        //     </RoleBasedRoute>
        //   ),
        // },
        {
          path: '/roles-management/:roleId',
          element: (
            <RoleBasedRoute permissions={[isActive, isAdmin]}>
              <RolesDetail />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/risk-hub',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <RiskHub />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/location-risk-hub',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <RiskHub type="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/financials-corporate',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <FinancialsCorporate />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:corporateName/:corporateId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DivisionFinancials />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:corporateName/:corporateId/:divisionName/:divisionId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProductGroupFinancials />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:corporateName/:corporateId/:divisionName/:divisionId/:productGroupName/:productGroupId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProductFinancials />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/data-inputs/it-applications',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ITApplications />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/data-inputs/hosting-entities-sub-entities',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <HostingLocations />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/data-inputs/business-functions',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctions />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/data-inputs/qualitative-factors',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactors />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/data-inputs/tiers',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <TiersDashboard />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:corporateId/all-financials',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <AllFinancials />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/bcp-hub',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BCPSelectionPage />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/bcp-landing/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BCPLanding />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/mission-scope/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Mission />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/plan-activation/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Activation />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/crisis-management/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Management />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/crisis-communication/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CrisisCommunication />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/disaster-recovery/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DisasterRecoveryPlan />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/emergency-response/:type/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <EmergencyResponsePlan />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/location/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BCPProcessAreas />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <StrategyReview />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId/facilities/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <StrategyFacility />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId/production/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Production />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId/people/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <PeoplePA />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId/it-resources/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ItResources />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId/supply-chain/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SupplyChainPA />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/process-areas-and-strategies/:locationId/:processAreaId/other-considerations/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <OtherConsiderations />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/strategies/location/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Strategies />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/strategy-summary/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <StrategySummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/facilities/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <StrategyFacility strategyType="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/production/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Production strategyType="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/people/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <PeoplePA strategyType="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/it-resources/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ItResources strategyType="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/supply-chain/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SupplyChainPA strategyType="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/other-considerations/:locationId/:strategyId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <OtherConsiderations strategyType="location" />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/data-inputs',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DataInputs />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/locations',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Locations />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/locations/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessAreas />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/processes/:processAreaId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Processes />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/suppliers',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <Suppliers />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/mitigating-locations',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <MitigatingLocations />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/exposures',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ExposureQuantification />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/division-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DivisionSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/division-selection/:divisionId/dependencies-tree',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DependenciesTree />,
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/division-selection/:divisionId/finished-goods-locations',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <FinishedGoodsLocations />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/division-selection/:divisionId/finished-goods-locations/finished-goods-mapping',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <FinishedGoodsMapping />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/finished-goods-summary/:divisionId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <FinishedGoodsSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/:divisionId/:parentLocation/level-locations',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LevelLocations />
            </RoleBasedRoute>
          ),
        },
        // {
        //   path: '/:divisionId/:parentLocation/level-locations',
        //   element: (
        //     <RoleBasedRoute permissions={[isActive]}>
        //       <LevelLocationMapping />
        //     </RoleBasedRoute>
        //   ),
        // },
        {
          path: '/location-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/location-selection/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessAreasDependencies />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/location-selection/:locationId/process-areas-summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessAreasSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/location-selection/:locationId/process-areas-summary/:processAreaId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessesAndEquipment />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/suppliers-dependencies',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SupplierDependencies />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/suppliers-dependencies/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SupplierMapping />,
            </RoleBasedRoute>
          ),
        },
        {
          path: '/suppliers-dependencies/:locationId/mapping',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SuppliersMappingPercentages />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/suppliers-dependencies/:locationId/summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SupplierDependenciesSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/:divisionId/:parentLocation/level-mapping',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LevelLocationMapping />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/dependency-mapping/:parentLocation/level-summary/:divisionId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LevelSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/finished-goods-inventory',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <FinishedGoodsInventory />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/finished-goods-inventory/:divisionId/division-detail',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DivisionDetail />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/internal-external-mitigation',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <InternalExternalMitigation />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:divisionId/mitigation',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <AutomotiveDivision />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/mitigation/:divisionId/location-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <MitigationLocationSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:divisionId/internal-mitigation',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <InternalMitigationTable />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:divisionId/external-mitigation',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ExternalMitigationTable />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:divisionId/external-mitigators',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <AutomotiveDivisionLocations />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/pa-location-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <PALocationSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/mitigation/:locationId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <PALocation />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/mitigation/:locationId/:mitigationType/:processAreaId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <PAMitigation />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/corporate-view',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CorporateView />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/division-view',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DivisionView />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:divisionId/product-group-view',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProductGroupView />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/:divisionId/product-group-view/:productGroupId/product-view',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProductView />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/exposure-summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ExposureSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/exposure-summary/process-areas',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SummaryProcessArea />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/exposure-summary/processes',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SummaryProcesses />
            </RoleBasedRoute>
          ),
        },

        {
          path: '/exposure-summary/suppliers',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <SummarySuppliers />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubPage />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/business-functions-it-applications',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubBusinessFunctionsItApplications />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/it-applications',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubItApplications />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/it-applications/:applicationUUID/report',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubItApplicationReport />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/qualitative-factors-it-applications',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubQualitativeFactorsItApplications />
            </RoleBasedRoute>
          ),
        },

        {
          path: '/cyber-hub/process-areas-it-applications',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubProcessAreasItApplications />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/business-functions-report',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubBusinessFunctionsReport />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/qualitative-factors-report',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubQualitativeFactorsReport />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber-hub/process-areas-report',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberHubProcessAreasReport />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <CyberMenu />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <EnterpriseApplicationsLanding />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/applications',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <EnterpriseApplicationsList />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/it-applications/selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <EnterpriseApplications />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/it-applications/mapping',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <DependencyMapping />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/it-applications/summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <EnterpriseApplicationsSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/business-functions',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionsTagging />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/business-functions/:businessFunctionId/it-application-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionsTaggingITSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/business-functions/selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionsTaggingSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/business-functions/:businessFunctionId/it-application-description',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionsApplicationDescription />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/business-functions/bulk',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionsTaggingBulk />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/qualitative-factors',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorsOverview />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/qualitative-factors/:factorId/it-application-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorsSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/qualitative-factors/bulk',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorsBulk />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/qualitative-factors/:qualitativeFactorId/it-application-description',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorsDescription />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/application-workarounds',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ApplicationWorkaroundsLanding />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/application-workarounds/:applicationUUID',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ApplicationWorkaround />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationUUID/application-workarounds/:applicationUUID',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ApplicationWorkaround />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/application-workarounds/business-functions',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <WorkaroundsBusinessFunctionsList />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/application-workarounds/business-functions/:businessFunctionId/summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <WorkaroundsBusinessFunctionsSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/list',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationLevelList />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/locations/selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationLevelSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/options',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationLevelOptions />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/application-workarounds',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationLevelApplicationWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/application-mapping',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationLevelLanding />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/qualitative-factors',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationQualitativeFactors />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/qualitative-factors/bulk',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationQualitativeFactorsBulk />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/qualitative-factors/:factorId/it-application-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationQualitativeFactorsSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/qualitative-factors/:factorId/it-application-description',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationQualitativeFactorsDescription />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/it-applications/selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationApplicationSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/it-applications/mapping',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationApplicationMapping />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/it-applications/summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationApplicationSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/process-areas-and-processes',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationProcessAreas />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/process-areas-and-processes/:processAreaId/it-application-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationProcessAreasSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/process-areas-and-processes/:processAreaId/it-application-tagging',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationProcessAreasTagging />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/process-areas-and-processes/:processAreaId/summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationProcessAreasSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/process-areas-and-processes/summary',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationProcessAreasSummary />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationBusinessFunctions />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions/:businessFunctionId/it-application-selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationBusinessFunctionsITSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions/:businessFunctionId/it-application-description',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationBusinessFunctionsApplicationDescription />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions/bulk',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationBusinessFunctionsBulk />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions/selection',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <LocationBusinessFunctionsSelection />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions-workarounds/:businessFunctionId',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionWorkaroundsItApplications />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationId/business-functions-workarounds',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <BusinessFunctionWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/qualitative-factors/workarounds',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorWorkaroundStats />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationUUID/qualitative-factors/workarounds',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorWorkaroundStats />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/enterprise-level/qualitative-factors/workarounds/:qualitativeFactorUUID',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationUUID/qualitative-factors/workarounds/:qualitativeFactorUUID',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <QualitativeFactorWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationUUID/process-areas-and-processes/workarounds',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessAreaAndProcessesWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationUUID/process-areas-and-processes/workarounds/:processAreaUUID/:processUUID',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessAreaOrProcessWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/cyber/location-level/:locationUUID/process-areas-and-processes/workarounds/:processAreaUUID',
          element: (
            <RoleBasedRoute permissions={[isActive]}>
              <ProcessAreaOrProcessWorkarounds />
            </RoleBasedRoute>
          ),
        },
        {
          path: '/access-denied',
          element: <AccessDenied />,
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ]);

  return <RouterProvider router={routes} />;
};

export default AppRoutes;
